import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Responsive, Layout, Layouts } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import {
    Activity,
    AlertCircle,
    ArrowLeftRight,
    Battery,
    BatteryFull,
    ChartNoAxesCombined,
    ChevronDown,
    ChevronUp,
    Cpu,
    Gauge,
    Heading,
    LandPlot,
    LayoutGrid,
    LineChart,
    Map as MapIcon,
    MapPin,
    MoreVertical,
    Navigation,
    RefreshCw,
    Route,
    Settings,
    Thermometer,
    Timer,
    TrendingUp,
    Weight,
    WeightIcon,
    Wifi,
    WifiOff,
    Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import moment from 'moment'

import Header from '../header'
import {
    useGetDevice,
    useGetDeviceErrorCodes,
} from '../../../../api/routes/devices'
import {
    DashboardWidget,
    useGetDashboards,
} from '../../../../api/routes/dashboards'
import { useGetModelAttributes } from '../../../../api/routes/model-attributes'
import { useSocketRoom } from '../../../../hooks/useSocketRoom.ts'
import DeviceError from './device-error'
import Loader from './loader'
import MapComponent from './map-component'

type Breakpoint = 'lg' | 'sm'
type WidgetType = DashboardWidget['type']

type ModelAttribute = {
    id: string
    name: string
    friendlyName?: string | null
    dataType?: string | null
    unit?: string | null
    trueValue?: string | null
    falseValue?: string | null
    trueDataValue?: string | null
    falseDataValue?: string | null
}

type LiveDataStateStatistics = {
    lastDataType: string | null
    lastUpdated: number | null
    messageCount: number
    connectionState?: string | null
}

type LiveDataKeyValues = {
    value: unknown
    timestamp: number
}

type LiveDataValues = Record<string, LiveDataKeyValues[]>

type LiveDataState = {
    statistics: LiveDataStateStatistics
    values: LiveDataValues
}

type DeviceErrorCode = {
    code: string
    title: string
    description: string
    number: number
    device_model_id: number
}

type ValueGroupItem = {
    attributeKey?: string | null
    label?: string | null
}

const BREAKPOINTS: Record<Breakpoint, number> = { lg: 996, sm: 0 }
const COLS: Record<Breakpoint, number> = { lg: 12, sm: 6 }
const BP_ORDER: Breakpoint[] = ['lg', 'sm']
const ROW_HEIGHT = 60
const VALUE_GROUP_DEFAULT_COLUMNS = 2
const VALUE_GROUP_DEFAULT_ROWS = 3
const VALUE_GROUP_EMPTY_LABEL = 'Select an attribute'

const WIDGET_SIZES: Record<
    WidgetType,
    { w: number; h: number; minW: number; minH: number; fullWidth?: boolean }
> = {
    metric: { w: 3, h: 2, minW: 2, minH: 2 },
    chart: { w: 5, h: 4, minW: 3, minH: 3 },
    map: { w: 4, h: 5, minW: 3, minH: 4 },
    status: { w: 4, h: 4, minW: 2, minH: 3 },
    header: { w: 12, h: 1, minW: 2, minH: 1, fullWidth: true },
    valueGroup: { w: 6, h: 4, minW: 1, minH: 2 },
}

const WIDGET_ICONS: Record<WidgetType, LucideIcon> = {
    metric: Gauge,
    chart: LineChart,
    map: MapIcon,
    status: AlertCircle,
    header: Heading,
    valueGroup: LayoutGrid,
}

const ICON_OPTIONS: Record<string, LucideIcon> = {
    activity: Activity,
    'alert-circle': AlertCircle,
    battery: Battery,
    'battery-full': BatteryFull,
    'chart-no-axes-combined': ChartNoAxesCombined,
    'chevron-down': ChevronDown,
    'chevron-up': ChevronUp,
    cpu: Cpu,
    gauge: Gauge,
    'land-plot': LandPlot,
    'layout-grid': LayoutGrid,
    'line-chart': LineChart,
    map: MapIcon,
    'map-pin': MapPin,
    'more-vertical': MoreVertical,
    navigation: Navigation,
    'refresh-cw': RefreshCw,
    route: Route,
    settings: Settings,
    'settings-suggest': Settings,
    'swap-horizontal': ArrowLeftRight,
    thermometer: Thermometer,
    timer: Timer,
    'trending-up': TrendingUp,
    weight: Weight,
    'weight-icon': WeightIcon,
    wifi: Wifi,
    'wifi-off': WifiOff,
    zap: Zap,
}

const emptyLiveData = (): LiveDataState => ({
    statistics: { lastDataType: null, lastUpdated: null, messageCount: 0 },
    values: {},
})

const asRecord = (value: unknown): Record<string, unknown> =>
    value !== null && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : {}

const numberOr = (value: unknown, fallback: number) => {
    const next = Number(value)
    return Number.isFinite(next) ? next : fallback
}

const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(value, max))

const titleOverride = (title?: string | null) =>
    typeof title === 'string' ? title.trim() : ''

const iconForWidget = (widget: DashboardWidget) => {
    const iconKey =
        typeof widget.options?.icon === 'string'
            ? widget.options.icon
            : null
    if (iconKey === 'none') return null
    return iconKey ? ICON_OPTIONS[iconKey] ?? WIDGET_ICONS[widget.type] : WIDGET_ICONS[widget.type]
}

const toLiveEntry = (value: unknown): LiveDataKeyValues => {
    const record = asRecord(value)
    if ('value' in record) {
        return {
            value: record.value,
            timestamp: numberOr(record.timestamp, Date.now()),
        }
    }

    return { value, timestamp: Date.now() }
}

const parseStatistics = (value: unknown): LiveDataStateStatistics => {
    const record = asRecord(value)
    return {
        lastDataType:
            typeof record.lastDataType === 'string'
                ? record.lastDataType
                : null,
        lastUpdated:
            record.lastUpdated === null || record.lastUpdated === undefined
                ? null
                : numberOr(record.lastUpdated, 0),
        messageCount: numberOr(record.messageCount, 0),
        connectionState:
            typeof record.connectionState === 'string'
                ? record.connectionState
                : null,
    }
}

const normalizeCurrentState = (payload: unknown): LiveDataState => {
    const record = asRecord(payload)
    const rawValues = asRecord(record.values)
    const values: LiveDataValues = {}

    for (const [key, value] of Object.entries(rawValues)) {
        values[key] = [toLiveEntry(value)]
    }

    return {
        statistics: parseStatistics(record.statistics),
        values,
    }
}

const storedLayoutForBreakpoint = (
    widget: DashboardWidget,
    bp: Breakpoint
) => {
    if (bp === 'lg') return widget.layouts?.lg ?? widget.layouts?.md
    return (
        widget.layouts?.sm ??
        widget.layouts?.xs ??
        widget.layouts?.xxs ??
        widget.layouts?.lg
    )
}

const normalizeLayoutItem = (
    layout: Partial<Layout> | undefined,
    widget: DashboardWidget,
    bp: Breakpoint
): Layout => {
    const def = WIDGET_SIZES[widget.type]
    const cols = COLS[bp]
    const minW = Math.min(def.minW, cols)
    const fallbackW = def.fullWidth ? cols : def.w
    const w = clamp(numberOr(layout?.w, fallbackW), minW, cols)
    const h = Math.max(numberOr(layout?.h, def.h), def.minH)
    const x = clamp(numberOr(layout?.x, 0), 0, Math.max(0, cols - w))
    const y = Math.max(0, numberOr(layout?.y, 0))

    return {
        i: widget.i,
        x,
        y,
        w,
        h,
        minW,
        minH: def.minH,
    }
}

const widgetsToLayouts = (widgets: DashboardWidget[]): Layouts => {
    const layouts: Layouts = {}
    for (const bp of BP_ORDER) {
        layouts[bp] = widgets.map((widget) =>
            normalizeLayoutItem(storedLayoutForBreakpoint(widget, bp), widget, bp)
        )
    }
    return layouts
}

const getValueGroupColumns = (widget: DashboardWidget) =>
    clamp(numberOr(widget.options?.columns, VALUE_GROUP_DEFAULT_COLUMNS), 1, 6)

const getValueGroupRows = (widget: DashboardWidget) =>
    clamp(numberOr(widget.options?.rows, VALUE_GROUP_DEFAULT_ROWS), 1, 10)

const getValueGroupItems = (widget: DashboardWidget): ValueGroupItem[] => {
    const values = widget.options?.values
    if (!Array.isArray(values)) return []
    return values.map((value) => {
        const record = asRecord(value)
        return {
            attributeKey:
                typeof record.attributeKey === 'string'
                    ? record.attributeKey
                    : null,
            label: typeof record.label === 'string' ? record.label : null,
        }
    })
}

const labelForAttribute = (attribute?: ModelAttribute | null) =>
    attribute?.friendlyName || attribute?.name || null

const formatTimestamp = (timestamp?: number | null) => {
    if (!timestamp || !Number.isFinite(Number(timestamp))) return 'N/A'
    return moment(timestamp).format('MMM D, YYYY HH:mm:ss')
}

const trimString = (value?: string | null) =>
    typeof value === 'string' ? value.trim() : ''

const matchesDataValue = (value: unknown, expected?: string | null) => {
    const expectedValue = trimString(expected)
    if (!expectedValue) return false
    return String(value).trim().toLowerCase() === expectedValue.toLowerCase()
}

const formatBooleanValue = (value: unknown, attribute: ModelAttribute) => {
    const trueLabel = trimString(attribute.trueValue) || 'Yes'
    const falseLabel = trimString(attribute.falseValue) || 'No'
    const normalizedValue = String(value).trim().toLowerCase()

    if (matchesDataValue(value, attribute.trueDataValue)) return trueLabel
    if (matchesDataValue(value, attribute.falseDataValue)) return falseLabel

    if (value === true || value === 1 || normalizedValue === '1' || normalizedValue === 'true') {
        return trueLabel
    }
    if (value === false || value === 0 || normalizedValue === '0' || normalizedValue === 'false') {
        return falseLabel
    }

    return String(value)
}

const formatLiveValue = (
    value: unknown,
    attribute?: ModelAttribute | null
) => {
    if (value === null || value === undefined || value === '') return '--'

    const type = attribute?.dataType?.toLowerCase() ?? ''
    if (type.includes('bool')) {
        return formatBooleanValue(value, attribute)
    }

    if (typeof value === 'number') {
        return Number.isInteger(value)
            ? value.toLocaleString()
            : value.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
              })
    }

    if (typeof value === 'object') {
        const record = asRecord(value)
        if ('latitude' in record && 'longitude' in record) {
            return `${record.latitude}, ${record.longitude}`
        }
        return JSON.stringify(value)
    }

    return String(value)
}

const toNumericSeries = (values: LiveDataKeyValues[]) =>
    values
        .map((entry) => Number(entry.value))
        .filter((value) => Number.isFinite(value))
        .slice(-40)

const TooltipValue = ({
    value,
    lastUpdated,
}: {
    value: string
    lastUpdated?: number | null
}) => {
    const [showTooltip, setShowTooltip] = useState(false)
    const timerRef = useRef<number | null>(null)

    const handleMouseEnter = () => {
        timerRef.current = window.setTimeout(() => setShowTooltip(true), 1000)
    }

    const handleMouseLeave = () => {
        if (timerRef.current) window.clearTimeout(timerRef.current)
        setShowTooltip(false)
    }

    return (
        <span
            className="relative cursor-default"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span>{value}</span>
            {showTooltip && (
                <span className="absolute left-1/2 z-10 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-sm">
                    Last updated: {formatTimestamp(lastUpdated)}
                </span>
            )}
        </span>
    )
}

const Sparkline = ({ values }: { values: number[] }) => {
    if (values.length < 2) {
        return (
            <div className="flex h-full min-h-24 items-center justify-center rounded-md border bg-background/80 text-xs text-muted-foreground">
                No trend yet
            </div>
        )
    }

    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = max - min || 1
    const points = values
        .map((value, index) => {
            const x = (index / (values.length - 1)) * 100
            const y = 100 - ((value - min) / span) * 88 - 6
            return `${x},${y}`
        })
        .join(' ')

    return (
        <svg
            className="h-full min-h-24 w-full rounded-md border bg-background/80"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
        >
            <polyline
                points={points}
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2.5"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    )
}

const StatusPill = ({
    tone,
    children,
}: {
    tone: 'ok' | 'error' | 'neutral'
    children: string
}) => {
    const className =
        tone === 'ok'
            ? 'border-green-200 bg-green-50 text-green-700'
            : tone === 'error'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-border bg-muted text-muted-foreground'

    return (
        <span
            className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}
        >
            {children}
        </span>
    )
}

type WidgetRendererProps = {
    widget: DashboardWidget
    attributesByName: Map<string, ModelAttribute>
    values: LiveDataValues
    latestGps: LiveDataKeyValues | null
    deviceName: string
    isConnected: boolean
    statistics: LiveDataStateStatistics
    errorCodes: DeviceErrorCode[]
    errorValue: number | null
    isMapFullscreen: boolean
    setIsMapFullscreen: (value: boolean) => void
}

const WidgetRenderer = ({
    widget,
    attributesByName,
    values,
    latestGps,
    deviceName,
    isConnected,
    statistics,
    errorCodes,
    errorValue,
    isMapFullscreen,
    setIsMapFullscreen,
}: WidgetRendererProps) => {
    const Icon = iconForWidget(widget)
    const attribute = widget.attributeKey
        ? attributesByName.get(widget.attributeKey)
        : null
    const label =
        titleOverride(widget.title) ||
        labelForAttribute(attribute) ||
        VALUE_GROUP_EMPTY_LABEL
    const entries = widget.attributeKey ? values[widget.attributeKey] ?? [] : []
    const latest = entries[entries.length - 1] ?? null
    const displayValue = formatLiveValue(latest?.value, attribute)
    const unit = attribute?.unit ?? ''

    if (widget.type === 'header') {
        const subtitle =
            typeof widget.options?.subtitle === 'string'
                ? widget.options.subtitle
                : ''

        return (
            <div className="flex h-full w-full items-center text-left">
                <div className="min-w-0">
                    <h2 className="truncate text-sm font-medium uppercase text-muted-foreground">
                        {titleOverride(widget.title) || 'Section Header'}
                    </h2>
                    {subtitle && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
        )
    }

    if (widget.type === 'metric') {
        return (
            <div className="h-full w-full rounded-lg border bg-card p-3 text-left sm:p-4">
                <div className="flex h-full items-start justify-between">
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium uppercase text-muted-foreground">
                            {label}
                        </p>
                        <p className="mt-1 text-lg text-foreground sm:text-2xl">
                            <TooltipValue
                                value={displayValue}
                                lastUpdated={latest?.timestamp}
                            />
                            {unit && displayValue !== '--' && (
                                <span className="ml-1 text-sm text-muted-foreground sm:text-lg">
                                    {unit}
                                </span>
                            )}
                        </p>
                    </div>
                    {Icon && (
                        <Icon className="ml-2 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    )}
                </div>
            </div>
        )
    }

    if (widget.type === 'chart') {
        return (
            <div className="flex h-full w-full flex-col gap-3 rounded-lg border bg-card p-4 text-left sm:p-5">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="truncate text-xs font-medium uppercase text-muted-foreground">
                            {label}
                        </p>
                        <p className="mt-1 truncate text-sm text-foreground">
                            <TooltipValue
                                value={displayValue}
                                lastUpdated={latest?.timestamp}
                            />
                            {unit && displayValue !== '--' && (
                                <span className="ml-1 text-xs text-muted-foreground">
                                    {unit}
                                </span>
                            )}
                        </p>
                    </div>
                    {Icon && (
                        <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    )}
                </div>
                <div className="min-h-0 flex-1">
                    <Sparkline values={toNumericSeries(entries)} />
                </div>
            </div>
        )
    }

    if (widget.type === 'valueGroup') {
        const columns = getValueGroupColumns(widget)
        const rows = getValueGroupRows(widget)
        const items = getValueGroupItems(widget)
        const itemCount = columns * rows
        const groupTitle = titleOverride(widget.title)

        return (
            <div className="flex h-full w-full flex-col rounded-lg border bg-card p-2 text-left sm:p-3">
                {groupTitle && (
                    <div className="mb-3 flex items-start justify-between gap-2 sm:mb-4">
                        <h3 className="min-w-0 truncate text-xs font-medium uppercase text-muted-foreground">
                            {groupTitle}
                        </h3>
                        {Icon && (
                            <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        )}
                    </div>
                )}
                <div
                    className="grid min-h-0 flex-1 content-start gap-x-3 gap-y-2 overflow-hidden sm:gap-x-5 sm:gap-y-3"
                    style={{
                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    }}
                >
                    {Array.from({ length: itemCount }).map((_, index) => {
                        const item = items[index]
                        if (!item?.attributeKey) {
                            return <div key={index} className="min-w-0" />
                        }

                        const itemAttribute = item?.attributeKey
                            ? attributesByName.get(item.attributeKey)
                            : null
                        const itemLabel =
                            titleOverride(item?.label) ||
                            labelForAttribute(itemAttribute) ||
                            ''
                        const itemEntries = item?.attributeKey
                            ? values[item.attributeKey] ?? []
                            : []
                        const itemLatest =
                            itemEntries[itemEntries.length - 1] ?? null
                        const itemValue = formatLiveValue(
                            itemLatest?.value,
                            itemAttribute
                        )
                        const itemUnit = itemAttribute?.unit ?? ''

                        return (
                            <div key={index} className="min-w-0">
                                <p className="truncate text-[11px] font-medium uppercase text-muted-foreground">
                                    {itemLabel}
                                </p>
                                <p className="mt-0.5 truncate text-sm font-medium text-foreground sm:text-base">
                                    <TooltipValue
                                        value={itemValue}
                                        lastUpdated={itemLatest?.timestamp}
                                    />
                                    {itemUnit && itemValue !== '--' && (
                                        <span className="ml-1 text-xs text-muted-foreground">
                                            {itemUnit}
                                        </span>
                                    )}
                                </p>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    if (widget.type === 'map') {
        return (
            <div className="relative h-full w-full overflow-hidden rounded-lg border bg-card">
                <MapComponent
                    data={latestGps ? [latestGps] : null}
                    deviceName={deviceName}
                    style={{ height: '100%', minHeight: '100%' }}
                    isFullScreen={isMapFullscreen}
                    setIsFullScreen={setIsMapFullscreen}
                />
                {/* <div className="absolute left-3 top-3 z-[500] flex items-center gap-1.5 rounded-md bg-white/90 px-2 py-1 text-xs font-medium text-muted-foreground shadow-sm">
                    <Icon className="h-3.5 w-3.5" />
                    {titleOverride(widget.title) || 'Live Map'}
                </div> */}
            </div>
        )
    }

    const errorDetails =
        errorValue == null
            ? null
            : errorCodes.find((error) => error.number === errorValue)

    return (
        <div className="flex h-full w-full flex-col gap-3 rounded-lg border bg-card p-4 text-left sm:p-5">
            <div className="flex items-start justify-between gap-3">
                <p className="truncate text-xs font-medium uppercase text-muted-foreground">
                    {titleOverride(widget.title) || 'Status / Errors'}
                </p>
                {Icon && (
                    <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                )}
            </div>
            <div className="grid min-h-0 flex-1 content-center gap-2">
                <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-foreground">
                        Connection
                    </span>
                    <StatusPill tone={isConnected ? 'ok' : 'neutral'}>
                        {isConnected ? 'ONLINE' : 'OFFLINE'}
                    </StatusPill>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-foreground">
                        Error
                    </span>
                    <StatusPill tone={errorDetails ? 'error' : 'ok'}>
                        {errorDetails ? errorDetails.code : 'OK'}
                    </StatusPill>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-foreground">
                        Last data
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                        {statistics.lastDataType || 'N/A'}
                    </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-foreground">
                        Messages
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                        {statistics.messageCount.toLocaleString()}
                    </span>
                </div>
            </div>
        </div>
    )
}

export const SingleDeviceLiveRoute = () => {
    const { id: deviceId } = useParams()
    const resolvedDeviceId = deviceId ?? ''
    const { data: device, isLoading: isDeviceLoading } = useGetDevice(
        resolvedDeviceId
    )
    const modelId =
        typeof device?.model?.id === 'string' ? device.model.id : undefined
    const { data: dashboards, isLoading: dashboardsLoading } =
        useGetDashboards(modelId)
    const { data: attributesRaw, isLoading: attributesLoading } =
        useGetModelAttributes(modelId ?? '')
    const { data: deviceErrorCodes } = useGetDeviceErrorCodes(
        resolvedDeviceId
    )

    const [data, setData] = useState<LiveDataState>(() => emptyLiveData())
    const [isPageLoading, setIsPageLoading] = useState(true)
    const [isMapFullscreen, setIsMapFullscreen] = useState(false)
    const [gridWidth, setGridWidth] = useState(0)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)

    const setCanvasRef = useCallback((el: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect()
        if (!el) return
        setGridWidth(el.clientWidth)
        const observer = new ResizeObserver(() => setGridWidth(el.clientWidth))
        observer.observe(el)
        resizeObserverRef.current = observer
    }, [])

    useEffect(
        () => () => {
            resizeObserverRef.current?.disconnect()
        },
        []
    )

    useSocketRoom(`device:${resolvedDeviceId}`, {
        events: {
            currentState: (payload: unknown) => {
                setData(normalizeCurrentState(payload))
            },
            message: (payload: unknown) => {
                const message = asRecord(payload)
                const key = typeof message.key === 'string' ? message.key : null
                if (!key) return

                setData((prevData) => {
                    const nextValues = { ...prevData.values }
                    const entries = [...(nextValues[key] ?? [])]
                    entries.push({
                        value: message.value,
                        timestamp: numberOr(message.timestamp, Date.now()),
                    })
                    if (entries.length > 100) entries.shift()
                    nextValues[key] = entries

                    return {
                        statistics: {
                            ...prevData.statistics,
                            lastUpdated: Date.now(),
                            messageCount:
                                prevData.statistics.messageCount + 1,
                            lastDataType: key,
                        },
                        values: nextValues,
                    }
                })
            },
        },
    })

    useEffect(() => {
        const timer = window.setTimeout(() => setIsPageLoading(false), 500)
        return () => window.clearTimeout(timer)
    }, [])

    const attributes = useMemo(
        () => (attributesRaw ?? []) as ModelAttribute[],
        [attributesRaw]
    )
    const attributesByName = useMemo(
        () =>
            new Map(
                attributes.map((attribute) => [attribute.name, attribute])
            ),
        [attributes]
    )
    const dashboard = useMemo(
        () =>
            dashboards?.find((item) => item.isDefault) ??
            dashboards?.[0] ??
            null,
        [dashboards]
    )
    const widgets = useMemo(() => dashboard?.widgets ?? [], [dashboard])
    const layouts = useMemo(() => widgetsToLayouts(widgets), [widgets])
    const deviceName =
        typeof device?.name === 'string' ? device.name : 'Device'
    const businessId =
        typeof device?.business?.id === 'string' ? device.business.id : undefined
    const errorCodes = (deviceErrorCodes ?? []) as DeviceErrorCode[]

    const getLatestEntry = useCallback(
        (key: string) => {
            const entries = data.values[key]
            return entries?.[entries.length - 1] ?? null
        },
        [data.values]
    )

    const latestGps = getLatestEntry('GPS')
    const errorValue = (() => {
        const latestError = getLatestEntry('Error')?.value
        if (latestError === null || latestError === undefined) return null
        const numericError = Number(latestError)
        return Number.isFinite(numericError) ? numericError : null
    })()
    const latestStatus = getLatestEntry('status')?.value
    const isConnected =
        data.statistics.connectionState === 'online' ||
        data.statistics.connectionState === 'connected' ||
        latestStatus === 'online'

    const loading =
        isPageLoading ||
        isDeviceLoading ||
        dashboardsLoading ||
        attributesLoading

    if (loading) {
        return (
            <div className="mx-auto px-4 py-6 sm:px-6 lg:px-8 lg:py-10 xl:px-32">
                <Header
                    deviceId={resolvedDeviceId}
                    name={
                        typeof device?.name === 'string' ? device.name : ''
                    }
                    businessId={device?.business?.id}
                />
                <Loader />
            </div>
        )
    }

    if (!device) {
        return (
            <div className="mx-auto px-4 py-6 sm:px-6 lg:px-8 lg:py-10 xl:px-32">
                <Header deviceId={resolvedDeviceId} name="" />
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <WifiOff
                        size={48}
                        className="mx-auto mb-3 text-muted-foreground"
                    />
                    <p className="text-muted-foreground">
                        Device not found or no data available.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="mx-auto px-4 py-6 sm:px-6 lg:px-8 lg:py-10 xl:px-32">
            <Header
                deviceId={resolvedDeviceId}
                name={deviceName}
                businessId={businessId}
            />

            <div className="mb-4 flex items-center justify-end gap-2">
                <span className="relative flex h-2.5 w-2.5">
                    {isConnected && (
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    )}
                    <span
                        className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                            isConnected ? 'bg-green-500' : 'bg-red-400'
                        }`}
                    />
                </span>
                <span
                    className={`text-xs font-medium ${
                        isConnected
                            ? 'text-green-700'
                            : 'text-muted-foreground'
                    }`}
                >
                    {isConnected ? 'Connected' : 'Disconnected'}
                </span>
            </div>

            {!dashboard ? (
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <LayoutGrid
                        size={48}
                        className="mx-auto mb-3 text-muted-foreground"
                    />
                    <p className="font-medium text-foreground">
                        No dashboard configured
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Create a dashboard for this model to show live widgets.
                    </p>
                </div>
            ) : widgets.length === 0 ? (
                <div className="rounded-lg border border-dashed p-12 text-center">
                    <LayoutGrid
                        size={48}
                        className="mx-auto mb-3 text-muted-foreground"
                    />
                    <p className="font-medium text-foreground">
                        This dashboard has no widgets
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Add widgets in the model dashboard designer.
                    </p>
                </div>
            ) : (
                <div ref={setCanvasRef} className="min-h-64">
                    {gridWidth > 0 && (
                        <Responsive
                            className="dash-live"
                            width={gridWidth}
                            layouts={layouts}
                            breakpoints={BREAKPOINTS}
                            cols={COLS}
                            rowHeight={ROW_HEIGHT}
                            margin={[12, 12]}
                            containerPadding={[0, 0]}
                            isDraggable={false}
                            isResizable={false}
                            compactType="vertical"
                        >
                            {widgets.map((widget) => (
                                <div
                                    key={widget.i}
                                    data-widget-type={widget.type}
                                    className="rounded-lg"
                                >
                                    <WidgetRenderer
                                        widget={widget}
                                        attributesByName={attributesByName}
                                        values={data.values}
                                        latestGps={latestGps}
                                        deviceName={deviceName}
                                        isConnected={isConnected}
                                        statistics={data.statistics}
                                        errorCodes={errorCodes}
                                        errorValue={errorValue}
                                        isMapFullscreen={isMapFullscreen}
                                        setIsMapFullscreen={setIsMapFullscreen}
                                    />
                                </div>
                            ))}
                        </Responsive>
                    )}
                </div>
            )}

            <DeviceError error={errorValue} errorCodes={errorCodes} />
        </div>
    )
}
